import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import { getCustomers, getCustomerById, updateCustomer } from '../../Services/Operations/ProductAPI';

const formatDateDMY = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  return timeString;
};

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-avatar"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Profile</th>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Bookings</th>
          <th>Joined</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonBookingDetails = () => (
  <div className="bookings-section skeleton">
    <div className="skeleton-booking-header"></div>
    <div className="skeleton-booking-table">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-booking-row">
          <div className="skeleton-booking-cell"></div>
          <div className="skeleton-booking-cell"></div>
          <div className="skeleton-booking-cell"></div>
          <div className="skeleton-booking-cell"></div>
        </div>
      ))}
    </div>
  </div>
);

const CustomerReport = () => {
  const { token } = useSelector(state => state.user);
  const [customers, setCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [pageSize] = useState(10);

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCustomers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm]);

  const fetchCustomers = async () => {
    try {
      setSearching(true);
      setLoading(true);
      
      const response = await getCustomers(token, currentPage, pageSize, searchTerm);

      if (response && response.users) {
        const transformedCustomers = response.users.map((user, index) => ({
          id: user._id,
          serialNo: getSequentialNumber(index),
          profileImage: user.profileImage,
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          bookingsCount: user.bookingsCount || 0,
          allBookings: user.allBookings || [],
          joined: formatDateDMY(user.createdAt),
          status: user.isVerified ? 'Active' : 'Inactive',
          isVerified: user.isVerified,
          walletBalance: user.walletBalance || 0,
          role: user.role,
        }));

        setCustomers(transformedCustomers);
        setTotalPages(response.totalPages || 1);
        setTotalCustomers(response.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch customers.');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleViewCustomer = (customer) => {
    setViewingCustomer({
      ...customer,
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      isVerified: customer.isVerified,
      walletBalance: customer.walletBalance || 0,
      role: customer.role || 'user',
      profileImage: customer.profileImage,
      bookingsCount: customer.bookingsCount || 0
    });
    
    setCustomerBookings(customer.allBookings || []);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (customers.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Serial No.', 'ID', 'Name', 'Email', 'Phone', 'Bookings', 'Joined', 'Status'];
    const csvData = customers.map(customer => [
      customer.serialNo,
      customer.id,
      customer.name || '',
      customer.email || '',
      customer.phone || '',
      customer.bookingsCount,
      customer.joined,
      customer.status
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customers-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer.id);
    setEditForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      status: customer.status
    });
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      const updateData = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        isVerified: editForm.status === 'Active'
      };

      const response = await updateCustomer(editingCustomer, updateData, token);

      if (response.success) {
        setCustomers(prevCustomers =>
          prevCustomers.map(customer =>
            customer.id === editingCustomer ? {
              ...customer,
              name: response.data?.name || editForm.name,
              email: response.data?.email || editForm.email,
              phone: response.data?.phone || editForm.phone,
              status: editForm.status,
              isVerified: response.data?.isVerified || (editForm.status === 'Active')
            } : customer
          )
        );

        setEditingCustomer(null);
        setEditForm({});
        alert('Customer updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update customer');
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Failed to update customer. Please try again.');
      alert('Failed to update customer. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCustomer(null);
    setEditForm({});
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeViewModal = () => {
    setViewingCustomer(null);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getBookingStatusBadge = (status) => {
    const statusMap = {
      'confirmed': 'success',
      'pending': 'warning',
      'cancelled': 'danger',
      'success': 'success',
      'failed': 'danger'
    };
    
    const badgeClass = statusMap[status?.toLowerCase()] || 'secondary';
    return (
      <span className={`status-badge ${badgeClass}`}>
        {status || 'N/A'}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'success': 'success',
      'pending': 'warning',
      'failed': 'danger',
      'refunded': 'info'
    };
    
    const badgeClass = statusMap[status?.toLowerCase()] || 'secondary';
    return (
      <span className={`status-badge ${badgeClass}`}>
        {status || 'N/A'}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getJourneyTime = (booking) => {
    if (booking.route?.origin?.timings) {
      const timing = booking.route.origin.timings[0];
      return timing?.departureTime || 'N/A';
    }
    return 'N/A';
  };

  if (loading && customers.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>
        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>
        <SkeletonTable />
        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Customer Report</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search customers by name, email or phone..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "70%" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Customers</h3>
          <p>{totalCustomers || customers.length}</p>
          <span>All registered customers</span>
        </div>
        <div className="stat-card">
          <h3>Active Customers</h3>
          <p>{customers.filter(c => c.status === 'Active').length}</p>
          <span>Active on current page</span>
        </div>
        <div className="stat-card">
          <h3>Verified Users</h3>
          <p>{customers.filter(c => c.isVerified).length}</p>
          <span>verified accounts</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{customers.length}</p>
          <span>customers found</span>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Profile</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Bookings</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer.id}>
                <td>{customer.serialNo}</td>
                <td>
                  <div className="profile-image-cell">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/uploads/${customer.profileImage}`}
                      alt={customer.name || 'Customer'}
                      className="customer-avatar"
                      onError={(e) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                </td>
                <td>
                  {editingCustomer === customer.id ? (
                    <input
                      type="text"
                      name="name"
                      value={editForm.name || ''}
                      onChange={handleInputChange}
                      className="edit-input"
                    />
                  ) : (
                    customer.name || 'N/A'
                  )}
                </td>
                <td>
                  {editingCustomer === customer.id ? (
                    <input
                      type="email"
                      name="email"
                      value={editForm.email || ''}
                      onChange={handleInputChange}
                      className="edit-input"
                    />
                  ) : (
                    customer.email || 'N/A'
                  )}
                </td>
                <td>
                  {editingCustomer === customer.id ? (
                    <input
                      type="text"
                      name="phone"
                      value={editForm.phone || ''}
                      onChange={handleInputChange}
                      className="edit-input"
                    />
                  ) : (
                    customer.phone || 'N/A'
                  )}
                </td>
                <td>{customer.bookingsCount}</td>
                <td>{customer.joined}</td>
                <td>
                  {editingCustomer === customer.id ? (
                    <select
                      name="status"
                      value={editForm.status || ''}
                      onChange={handleInputChange}
                      className="edit-select"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  ) : (
                    <span className={`status-badge ${customer.status ? customer.status.toLowerCase() : 'inactive'}`}>
                      {customer.status || 'Inactive'}
                    </span>
                  )}
                </td>
                <td>
                  {editingCustomer === customer.id ? (
                    <div className="edit-actions">
                      <button className="action-btn save" onClick={handleSaveEdit}>
                        Save
                      </button>
                      <button className="action-btn cancel" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleViewCustomer(customer)}
                      >
                        View
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={() => handleEditCustomer(customer)}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {customers.length === 0 && !loading && (
          <div className="no-data">
            <p>No customers found matching your search.</p>
          </div>
        )}
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Showing {customers.length} of {totalCustomers} customers (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {viewingCustomer && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Customer Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingBookings ? (
                <SkeletonBookingDetails />
              ) : (
                <>
                  <div className="customer-details">
                    <div className="customer-profile-image">
                      <img
                        src={viewingCustomer.profileImage ? 
                          `${process.env.REACT_APP_BASE_URL}/uploads/${viewingCustomer.profileImage}` : 
                          ''}
                        alt={''}
                        className="profile-image-large"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/default-avatar.png';
                        }}
                      />
                    </div>
                    <div className="detail-row">
                      <label>Name:</label>
                      <span>{viewingCustomer.name || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Email:</label>
                      <span>{viewingCustomer.email || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Phone:</label>
                      <span>{viewingCustomer.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Total Bookings:</label>
                      <span>{viewingCustomer.bookingsCount || 0}</span>
                    </div>
                    <div className="detail-row">
                      <label>Joined Date:</label>
                      <span>{viewingCustomer.joined}</span>
                    </div>
                    <div className="detail-row">
                      <label>Status:</label>
                      <span className={`status-badge ${viewingCustomer.status ? viewingCustomer.status.toLowerCase() : 'inactive'}`}>
                        {viewingCustomer.status || 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="bookings-section">
                    <h4>Customer Bookings ({viewingCustomer.bookingsCount || 0})</h4>
                    
                    {customerBookings.length > 0 ? (
                      <div className="bookings-table-container">
                        <table className="bookings-table">
                          <thead>
                            <tr>
                              <th>PNR Number</th>
                              <th>Booking Date</th>
                              <th>Booking Time</th>
                              <th>Journey Date</th>
                              <th>Journey Time</th>
                              <th>Bus</th>
                              <th>Route</th>
                              <th>Seats</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Payment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerBookings.map((booking, index) => (
                              <tr key={booking._id || index}>
                                <td className="pnr">{booking.pnr || 'N/A'}</td>
                                <td>{formatDateDMY(booking.createdAt)}</td>
                                <td>{formatDateTime(booking.createdAt).split(' ')[1] || 'N/A'}</td>
                                <td>{formatDateDMY(booking.journeyDate)}</td>
                                <td>{getJourneyTime(booking)}</td>
                                <td>
                                  <div>
                                    <div><strong>{booking.bus?.busName || 'N/A'}</strong></div>
                                    <small>{booking.bus?.busNumber || ''}</small>
                                    <div><small>{booking.bus?.busType || ''}</small></div>
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <div>{booking.route?.routeName || 'N/A'}</div>
                                    {booking.route?.origin && (
                                      <small>
                                        {booking.route.origin.city || booking.route.origin} → 
                                        {booking.route.destination?.city || booking.route.destination}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="seats-list">
                                    {booking.seats?.slice(0, 3).map((seat, idx) => (
                                      <span key={idx} className="seat-badge">
                                        {seat.seatNumber}
                                      </span>
                                    ))}
                                    {booking.seats?.length > 3 && (
                                      <span className="more-seats">
                                        +{booking.seats.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  {booking.pricing ? (
                                    <div>
                                      <div><strong>{formatCurrency(booking.pricing.payableAmount)}</strong></div>
                                      <div className="booking-details-small">
                                        <small>Total: {formatCurrency(booking.pricing.totalAmount)}</small>
                                      </div>
                                    </div>
                                  ) : 'N/A'}
                                </td>
                                <td>{getBookingStatusBadge(booking.status)}</td>
                                <td>{getPaymentStatusBadge(booking.paymentStatus)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {customerBookings.length > 0 && (
                          <div className="booking-summary">
                            <div className="summary-item">
                              <span>Total Bookings:</span>
                              <strong>{customerBookings.length}</strong>
                            </div>
                            <div className="summary-item">
                              <span>Total Amount:</span>
                              <strong>
                                {formatCurrency(customerBookings.reduce((total, booking) => 
                                  total + (booking.pricing?.payableAmount || 0), 0))}
                              </strong>
                            </div>
                            <div className="summary-item">
                              <span>Confirmed:</span>
                              <strong>
                                {customerBookings.filter(b => 
                                  b.status?.toLowerCase() === 'confirmed' || 
                                  b.status?.toLowerCase() === 'success'
                                ).length}
                              </strong>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-bookings">
                        <p>No bookings found for this customer.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerReport;